import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Zap, X, Loader2, Play, Link2, CheckCircle, XCircle,
} from 'lucide-react';
import { PayloadTemplateEditor } from './PayloadTemplateEditor';

const ActionTypeSelect = ({ form, update, actionOptions }) => (
  <select
    value={form.action_type}
    onChange={e => update('action_type', e.target.value)}
    className="w-full bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
    data-testid="vf-action-type-select"
  >
    {Object.entries(
      actionOptions.reduce((acc, o) => {
        acc[o.group] = acc[o.group] || [];
        acc[o.group].push(o);
        return acc;
      }, {})
    ).map(([group, opts]) => (
      <optgroup key={group} label={group}>
        {opts.map(o => (
          <option key={o.id} value={o.id}>
            {o.label}{o.status === 'stub' ? ' (coming soon)' : ''}
          </option>
        ))}
      </optgroup>
    ))}
  </select>
);

const ConnectionSelect = ({ form, updateCfg, eligibleConnections }) => (
  <div className="mt-3">
    <label className="text-xs text-white/50 mb-1 flex items-center gap-1">
      <Link2 className="w-3 h-3" /> Connection *
    </label>
    {eligibleConnections.length === 0 ? (
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2.5 text-xs text-amber-200">
        No enabled <strong>{form.action_type}</strong> connections yet.
        Open the <strong>Integrations</strong> tab to add one.
      </div>
    ) : (
      <select
        value={form.action_config?.connection_id || ''}
        onChange={e => updateCfg('connection_id', e.target.value)}
        className="w-full bg-[#0A0F1C] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-teal-500/50"
        data-testid="vf-connection-select"
      >
        <option value="">— select a connection —</option>
        {eligibleConnections.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
    )}
  </div>
);

const TestResultPanel = ({ testResult }) => {
  if (!testResult) return null;
  return (
    <div className={`mt-3 p-3 rounded-lg border text-xs ${testResult.matches ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-white/10 bg-white/5'}`}>
      <div className="flex items-center gap-2 mb-1">
        {testResult.matches
          ? <CheckCircle className="w-4 h-4 text-emerald-400" />
          : <XCircle className="w-4 h-4 text-white/30" />}
        <span className="font-semibold text-white">
          {testResult.matches ? 'MATCH' : 'No match'}
        </span>
        <span className="text-white/40">confidence: {Math.round((testResult.confidence || 0) * 100)}%</span>
      </div>
      <p className="text-white/60 mb-2 italic">{testResult.reason}</p>
      {testResult.extracted && Object.keys(testResult.extracted).length > 0 && (
        <pre className="text-white/60 bg-black/30 rounded p-2 overflow-x-auto">
          {JSON.stringify(testResult.extracted, null, 2)}
        </pre>
      )}
    </div>
  );
};

const DryRunPanel = ({ initial, onTest }) => {
  const [testing, setTesting] = useState(false);
  const [testTranscript, setTestTranscript] = useState('');
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    if (!testTranscript.trim()) { toast.error('Paste a transcript to test against'); return; }
    if (!initial.id) { toast.error('Save the rule first, then test.'); return; }
    setTesting(true);
    try {
      setTestResult(await onTest(initial.id, testTranscript));
    } catch {
      toast.error('Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="border-t border-white/10 pt-5">
      <p className="text-white/70 text-sm font-medium mb-2 flex items-center gap-2">
        <Play className="w-4 h-4 text-amber-400" /> Dry-run against a sample transcript
      </p>
      <textarea
        rows={4}
        value={testTranscript}
        onChange={e => setTestTranscript(e.target.value)}
        placeholder="Paste a call transcript here to test if this rule would match..."
        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/20 focus:outline-none focus:border-amber-500/50 font-mono text-xs"
      />
      <button
        onClick={handleTest}
        disabled={testing || !testTranscript.trim()}
        className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 text-amber-300 text-xs font-medium rounded-lg disabled:opacity-50"
        data-testid="vf-test-btn"
      >
        {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
        {testing ? 'Evaluating...' : 'Test Rule'}
      </button>
      <TestResultPanel testResult={testResult} />
    </div>
  );
};

export const RuleEditor = ({ initial, onClose, onSave, saving, onTest, connections, integrations, token }) => {
  const [form, setForm] = useState(initial);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateCfg = (k, v) => setForm(f => ({ ...f, action_config: { ...f.action_config, [k]: v } }));

  const actionOptions = useMemo(() => {
    const opts = [
      { id: 'log',     label: 'Log Only',    group: 'Built-in' },
      { id: 'webhook', label: 'Raw Webhook', group: 'Built-in' },
    ];
    (integrations || []).forEach(i => opts.push({
      id: i.id,
      label: i.label,
      group: i.category.charAt(0).toUpperCase() + i.category.slice(1),
      status: i.status,
    }));
    return opts;
  }, [integrations]);

  const eligibleConnections = useMemo(
    () => (connections || []).filter(c => c.integration_id === form.action_type && c.enabled),
    [connections, form.action_type]
  );

  const isCatalogIntegration = form.action_type !== 'log' && form.action_type !== 'webhook';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#0D1526] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#0D1526] z-10">
          <h2 className="text-white font-semibold flex items-center gap-2">
            <Zap className="w-4 h-4 text-yellow-400" />
            {initial.id ? 'Edit Rule' : 'New VoiceFabric Rule'}
          </h2>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="text-xs text-white/50 block mb-1">Rule name *</label>
            <input
              value={form.name}
              onChange={e => update('name', e.target.value)}
              placeholder="e.g. Scheduling Intent Detection"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
              data-testid="vf-rule-name"
            />
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Description</label>
            <input
              value={form.description}
              onChange={e => update('description', e.target.value)}
              placeholder="What this rule detects"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50"
            />
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Trigger prompt *</label>
            <textarea
              rows={4}
              value={form.trigger_prompt}
              onChange={e => update('trigger_prompt', e.target.value)}
              placeholder='e.g. "Does this transcript show the caller asking to book or schedule a meeting, appointment, or callback? If yes, extract the proposed time and contact info."'
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-teal-500/50 font-mono text-xs"
              data-testid="vf-rule-prompt"
            />
            <p className="text-white/30 text-xs mt-1">
              Write this like you're talking to a human analyst. Be specific about what counts as a match and what fields to extract.
            </p>
          </div>
          <div>
            <label className="text-xs text-white/50 block mb-1">Extract fields (optional, comma-separated)</label>
            <input
              value={(form.action_config?.extract_fields || []).join(', ')}
              onChange={e => updateCfg('extract_fields', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
              placeholder="proposed_time, contact_name, contact_phone, topic"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-white/20 focus:outline-none focus:border-teal-500/50"
            />
          </div>

          <div>
            <label className="text-xs text-white/50 block mb-2">Action</label>
            <ActionTypeSelect form={form} update={update} actionOptions={actionOptions} />

            {form.action_type === 'webhook' && (
              <input
                value={form.action_config?.webhook_url || ''}
                onChange={e => updateCfg('webhook_url', e.target.value)}
                placeholder="https://your-service.com/voicefabric-hook"
                className="mt-3 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm font-mono placeholder-white/20 focus:outline-none focus:border-teal-500/50"
                data-testid="vf-webhook-url"
              />
            )}

            {isCatalogIntegration && (
              <ConnectionSelect form={form} updateCfg={updateCfg} eligibleConnections={eligibleConnections} />
            )}

            {/* JSON template editor — only for webhook-style action types */}
            {['webhook', 'make', 'n8n'].includes(form.action_type) && (
              <PayloadTemplateEditor token={token} form={form} updateCfg={updateCfg} />
            )}
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              className={`w-10 h-5 rounded-full transition-colors relative ${form.enabled ? 'bg-teal-500' : 'bg-white/10'}`}
              onClick={() => update('enabled', !form.enabled)}
            >
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </div>
            <span className="text-sm text-white/70">{form.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>

          {initial.id && <DryRunPanel initial={initial} onTest={onTest} />}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#0D1526]">
          <button onClick={onClose} className="px-4 py-2 text-white/50 hover:text-white text-sm">Cancel</button>
          <button
            onClick={() => onSave(form)}
            disabled={saving || !form.name || !form.trigger_prompt}
            className="flex items-center gap-2 px-5 py-2 bg-teal-500 hover:bg-teal-400 disabled:bg-white/10 disabled:text-white/30 text-white text-sm font-medium rounded-lg"
            data-testid="vf-save-rule-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {initial.id ? 'Save Changes' : 'Create Rule'}
          </button>
        </div>
      </div>
    </div>
  );
};
